
import Vue from "vue";
import { Component, Inject, Prop, Watch } from "vue-property-decorator";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import { throttle, round } from "lodash";
import { ControlsTreeContext, FocusPointOptions } from "@/types/controls";
import { Image, ImageV } from "@/types/media";
import { uiStore } from "@/store/store-accessor";
import NumberInput from "@/components/Reusable/CustomInputs/NumberInput.vue";
import { EasyEvent } from "@/util/easy-event";
import Icon from "@/components/UI/Icon.vue";
import FocusPointGrid from "@/components/Reusable/CustomInputs/FocusPointInput/FocusPointGrid.vue";

@Component({
  components: { FocusPointGrid, Icon, NumberInput }
})
export default class FocusPointInput extends Vue {
  @Inject({
    from: "context",
    default: null
  })
  readonly context?: ControlsTreeContext;

  @Prop()
  value!: { x: number | null; y: number | null } | null;

  @Prop()
  options!: FocusPointOptions;

  bounds!: DOMRect;
  handlerBounds!: DOMRect;

  isActive = false;

  currentX = 0;
  currentY = 0;

  upperX!: number;
  upperY!: number;

  easyEvents = new EasyEvent();

  get container() {
    return this.$refs.container as HTMLElement;
  }

  get dragHandler() {
    return this.$refs.handler as HTMLElement;
  }

  get throttledUpdate() {
    return throttle(this.update, 20, {
      leading: true,
      trailing: false
    });
  }

  get canShowImage() {
    return this.image && !this.gridMode;
  }

  get gridMode() {
    return this.options?.gridMode;
  }

  get imagePath() {
    return this.options?.imagePropertyPath;
  }

  get imagePathWithBreakpoint() {
    return `${this.imagePath}.${uiStore.currentBreakpoint.id}`;
  }

  get image(): Image | null {
    if (!this.imagePath) return null;

    const image =
      this.context?.getControlValue(this.imagePathWithBreakpoint) ||
      this.context?.getControlValue(this.imagePath);

    return pipe(
      image,
      ImageV.decode,
      fold(
        () => null,
        validImage => validImage
      )
    );
  }

  get height(): number {
    if (this.image && this.canShowImage) {
      const fullSize = this.image.sizes.full;
      const ratio = fullSize.width / fullSize.height;
      // TODO: We need to use a hardcoded width because getBoundingClientRect fires too early and return zero.
      const targetWidth = 250;
      return targetWidth / ratio;
    }

    return 120;
  }

  @Watch("value")
  onValueUpdate() {
    if (!this.value) {
      this.positionDragHandler();
    }
  }

  mounted() {
    this.updateBounds();
    this.positionDragHandler();
  }

  beforeDestroy() {
    this.unbindListeners();
  }

  positionDragHandler() {
    // Position the drag handler based on the value present.
    if (this.value && this.value.x && this.value.y) {
      const left = this.bounds.width * (this.value.x / 100);
      const top = this.bounds.height * (this.value.y / 100);

      const x = left - this.handlerBounds.width / 2;
      const y = top - this.handlerBounds.height / 2;

      this.currentX = this.clamp(x, 0, this.upperX);
      this.currentY = this.clamp(y, 0, this.upperY);
      return;
    }

    // Otherwise center by default
    this.currentX = this.bounds.width / 2 - this.handlerBounds.width / 2;
    this.currentY = this.bounds.height / 2 - this.handlerBounds.height / 2;
  }

  onStart(event: MouseEvent | TouchEvent) {
    if (this.gridMode) return;
    this.updateBounds();

    if (event.target === this.dragHandler) {
      this.isActive = true;
    }

    this.bindListeners();
  }

  onChange(event: MouseEvent | TouchEvent) {
    if (this.gridMode) return;
    event.preventDefault();

    const { clientX, clientY } = this.getCoordinates(event);
    const x = clientX - this.bounds.left - this.handlerBounds.width / 2;
    const y = clientY - this.bounds.top - this.handlerBounds.height / 2;

    this.currentX = this.clamp(x, 0, this.upperX);
    this.currentY = this.clamp(y, 0, this.upperY);

    this.throttledUpdate();
  }

  onMove(event: MouseEvent) {
    if (this.gridMode) return;
    if (!this.isActive) return;
    this.onChange(event);
  }

  onEnd(event: MouseEvent) {
    event.preventDefault();

    this.isActive = false;
    this.unbindListeners();
  }

  clamp(num: number, lower: number, upper: number) {
    return Math.max(lower, Math.min(num, upper));
  }

  getCoordinates(event: any) {
    if (event.touches) {
      return {
        clientX: event.touches[0].clientX || 0,
        clientY: event.touches[0].clientY || 0
      };
    }

    return {
      clientX: event.clientX,
      clientY: event.clientY
    };
  }

  update() {
    const x = round((this.currentX / this.upperX) * 100, 2);
    const y = round((this.currentY / this.upperY) * 100, 2);

    this.$emit("input", { x, y });
  }

  updateVal(key: string, newValue: number | null) {
    this.$emit("input", {
      ...this.value,
      [key]: newValue === null ? null : round(newValue, 2)
    });

    this.$nextTick(() => this.positionDragHandler());
  }

  updateBounds() {
    this.bounds = this.container.getBoundingClientRect();
    this.handlerBounds = this.dragHandler.getBoundingClientRect();

    this.upperX = this.bounds.width - this.handlerBounds.width - 2;
    this.upperY = this.bounds.height - this.handlerBounds.height - 2;
  }

  bindListeners() {
    this.easyEvents.on("mouseup", this.onChange);
    this.easyEvents.on("mouseup", this.onEnd);
    this.easyEvents.on("mousemove", this.onMove);
  }

  unbindListeners() {
    this.easyEvents.removeAll();
  }

  onGridDotClicked({ x, y }: { x: number; y: number }) {
    this.$emit("input", { x, y });
  }
}
